<template>
  <div
    class="w-full lg:sticky top-[100px] lg:max-w-[480px] shrink-0 bg-surface-grey-light rounded-md p-6 space-y-6"
    ref="ticketAreaRef"
  >
    <div
      v-if="event.status_message_visible"
      class="bg-surface-light p-4 border-surface-grey border rounded-md flex items-start gap-2"
    >
      <InfoIcon class="w-5 h-5 shrink-0 fill-primary mt-[2px]" />
      <div class="font-semibold whitespace-pre-line">
        {{ props.event.status_message_text }}
      </div>
    </div>

    <div class="relative -mx-6 dashed-ticket-border" v-if="event.status_message_visible">
      <svg
        :class="[
          'absolute z-10 bottom-[1px] -right-[1px] translate-y-1/2 fill-surface-light ',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
      >
        <path
          d="M7.5 0.518555C3.59105 0.775917 0.5 4.02593 0.5 8C0.5 11.9741 3.59105 15.2241 7.5 15.4814V0.518555Z"
        />
      </svg>

      <svg
        :class="[
          'absolute bottom-[1px] -left-[1px] z-10 translate-y-1/2 fill-surface-light ',
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="7"
        height="16"
        viewBox="0 0 7 16"
        fill="none"
      >
        <path
          d="M-0.5 0.518555C3.40895 0.775917 6.5 4.02593 6.5 8C6.5 11.9741 3.40895 15.2241 -0.5 15.4814V0.518555Z"
        />
      </svg>
    </div>

    <div class="space-y-1">
      <!-- With an sso price it is shown above in the regular price's style, and the regular price becomes muted -->
      <div v-if="ssoPriceText" class="h3 wrap-anywhere">
        {{ ssoPriceText }}
      </div>

      <!-- One inline text flow, so a long label wraps together with its price instead of splitting into columns -->
      <div :class="[priceClass, 'wrap-anywhere']">
        <template v-if="showRegularPriceLabel">{{ regularPriceLabel }}{{ ' ' }}</template>
        <!-- The override has priority over the external ticketsale price, as in the old client -->
        <template v-if="event.price_override_active">{{ getLocalizedEventValue(event, locale, 'price_override_text') }}</template>
        <template v-else-if="isExternalTicketsale && event.external_ticketsale_price">{{ formatPrice(event.external_ticketsale_price, event.currency, $i18n.locale) }}</template>
        <template v-else>
          <template v-if="event.min_price_from">{{ $t('common.from') }}{{ ' ' }}</template>
          <template v-if="!event.hide_price">{{ formatPrice(event.price, event.currency, $i18n.locale) }}</template>
        </template>
        <span
          v-if="priceBefore"
          class="text-text line-through font-semibold"
        >{{ ' ' }}{{ $t('common.before') }} {{ formatPrice(priceBefore, event.currency, $i18n.locale) }}</span>
      </div>

      <div v-if="ssoStore.hasExclusiveMessage && event.single_sign_on_exclusive_message" class="space-y-1">
        <p class="text-base font-semibold text-headings wrap-anywhere">
          {{ event.single_sign_on_exclusive_message }}
        </p>
        <p v-if="exclusiveMessagePostfix" class="text-base text-text wrap-anywhere">
          {{ exclusiveMessagePostfix }}
        </p>
      </div>
              

    </div>

    <EventSsoLoginPrompt v-if="showLoginPrompt" :text="loginPromptText" />

    <BaseButton
      key="static"
      class="w-full relative group"
      :color="buttonState.color"
      size="lg"
      :disabled="!buttonState.active"
      @click="handleTicketClick"
      v-if="!event.eventpage_hide_find_tickets_button"
    >
      <span class="w-full flex items-center justify-between">
        <span class="text-wrap">{{ buttonState.text }}</span>
        <ArrowRightIcon
          class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
          v-if="buttonState.active"
        />
      </span>
    </BaseButton>

    <transition name="slidedown" mode="out-in" v-if="!event.eventpage_hide_find_tickets_button">
      <div
        v-if="isFixed"
        class="fixed mb-0 bottom-0 border-t border-surface-grey z-10 left-0 right-0 p-4 md:hidden bg-surface-light"
      >
        <BaseButton
          class="w-full"
          :color="buttonState.color"
          size="lg"
          :disabled="!buttonState.active"
          @click="handleTicketClick"
        >
          <span class="w-full flex items-center justify-between">
            <span class="text-wrap"> {{ buttonState.text }} </span>
            <ArrowRightIcon 
              class="w-4 h-4 ml-2 fill-current" 
              v-if="buttonState.active" 
            />
          </span>
        </BaseButton>
      </div>
    </transition>

    <WaitlistModal v-model="showWaitlistModal" :event="event" />

    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div class="min-w-0 space-y-2" v-if="event.saleend && !saleEnded && !saleNotStarted">
        <div class="flex flex-wrap items-center text-sm font-semibold" v-if="event.saleend_countdown">
          <StopwatchIcon class="w-5 h-5 shrink-0 fill-current mr-1" />
          <span class="mr-1">{{ $t('event.ends_in') }}</span>
          <BaseCountdown :date="event.saleend" />
        </div>
        <div class="flex items-start text-sm font-semibold" v-if="event.display_saleend && saleEndDate">
          <StopwatchIcon class="w-5 h-5 shrink-0 fill-current mr-1" />
          <span>{{ $t('event.ends_at') }} {{ saleEndDate }}</span>
        </div>
      </div>

      <div class="flex items-center" v-if="event.remaining_tickets && event.show_remaining_tickets"> 
        <TicketIcon class="w-5 h-5 shrink-0 fill-current mr-1" />
        <span class="text-sm font-semibold">
          {{ $t('event.x_tickets_left', { count: event.remaining_tickets }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import router from '@/router';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useGeneralStore } from '@/stores/general';
import { useSsoStore } from '@/stores/sso';
import type { EventDetail } from '@/types';
import { getLocalizedEventValue, getSalestart } from '@/utils/eventItemUtil';
import { formatDate, formatDateWithOffset } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { slugify } from '@/utils/slugify';
import { unixTimeToDate } from '@/utils/unixTimeToDate';
import { useSsoLabels } from '@/utils/useSsoLabels';

import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
import InfoIcon from '../icons/InfoIcon.vue';
import StopwatchIcon from '../icons/StopwatchIcon.vue';
import TicketIcon from '../icons/TicketIcon.vue';
import BaseButton, { type ButtonColor } from '../ui/BaseButton.vue';
import BaseCountdown from '../ui/BaseCountdown.vue';
import EventSsoLoginPrompt from './EventSsoLoginPrompt.vue';
import WaitlistModal from './WaitlistModal.vue';

const props = defineProps<{
  event: EventDetail;
  previewMode?: boolean;
}>();

const { locale, t: $t } = useI18n();

const isFixed = ref(false);
const ticketAreaRef = ref<HTMLElement | null>(null);
const now = ref(new Date());

let nowInterval: ReturnType<typeof setInterval>;

const checkout = useCheckoutStore();
const generalStore = useGeneralStore();
const ssoStore = useSsoStore();
const { ssoPriceLabel, regularPriceLabel, exclusiveMessagePostfix, loginPromptText, noAccessButtonText } = useSsoLabels();

const showWaitlistModal = ref(false);

const isExternalTicketsale = computed(() =>
  generalStore.customer().external_ticketsale === 1 && !!props.event.external_ticketsale_url
);

const hasWaitlist = computed(() => props.event.waitlists && props.event.waitlists.length > 0);

const priceBefore = computed(() => {
  return null;
}); 

// The sso price is shown next to the normal price, regardless of login. The override text replaces it (also for
// external ticketsale, where sso_prices is not used).
const ssoPriceText = computed<string | null>(() => {
  if (!ssoStore.isActive) {
    return null;
  }
  if (props.event.single_sign_on_price_override_active) {
    // The override replaces the sso price completely, even when its text is empty (nothing is shown then).
    return getLocalizedEventValue(props.event, locale.value, 'single_sign_on_price_override_text');
  }
  if (isExternalTicketsale.value || props.event.hide_price || !ssoStore.memberUsertype) {
    return null;
  }
  const price = props.event.sso_prices?.[ssoStore.memberUsertype]?.price;
  if (price === null || price === undefined) {
    return null;
  }
  const from = props.event.min_price_from ? `${$t('common.from')} ` : '';
  return `${ssoPriceLabel.value} ${from}${formatPrice(price, props.event.currency, locale.value)}`;
});

// With an sso price the regular price is muted (the style the sso price had before), otherwise it looks as it always did.
const priceClass = computed(() => ssoPriceText.value ? 'text-text font-semibold' : 'h3');

// The login/subscribe prompt is for logged out users on events that have an sso price (or its override) to miss out on.
const showLoginPrompt = computed(() =>
  ssoStore.isAvailable && !ssoStore.isLoggedIn && ssoPriceText.value !== null && !!loginPromptText.value,
);

// With an sso price the regular price gets a prefix, unless it is the organizer's override text (a full phrase already).
const showRegularPriceLabel = computed(() => {
  if (!ssoPriceText.value) {
    return false;
  }
  if (props.event.price_override_active) {
    return false;
  }
  if (isExternalTicketsale.value && props.event.external_ticketsale_price) {
    return true;
  }
  return !props.event.hide_price;
});

function handleTicketClick() {
  if (!buttonState.value.active) {
    return;
  }

  if (needsSsoLogin.value) {
    ssoStore.login();
    return;
  }

  if (isExternalTicketsale.value) {
    window.open(props.event.external_ticketsale_url, '_blank', 'noopener,noreferrer');
    return;
  }

  if ((props.event.soldout || saleEnded.value) && hasWaitlist.value) {
    showWaitlistModal.value = true;
    return;
  }

  checkout.setCheckoutID(props.event.id);

  const isClosedEvent = typeof props.event.id === 'string' && /^[0-9a-f]{96}$/.test(props.event.id);
  router.push({
    name: 'checkout-tickets',
    params: isClosedEvent
      ? { id: props.event.id }
      : { id: props.event.id, name: slugify(props.event.info1) },
  });
}

function handleScroll() {
  if (ticketAreaRef.value) {
    const rect = ticketAreaRef.value.getBoundingClientRect();
    const hasScrolledPast = rect.bottom <= 0;
    isFixed.value = hasScrolledPast;
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll();
  nowInterval = setInterval(() => { now.value = new Date(); }, 60_000);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  clearInterval(nowInterval);
});

// "find tickets" button
type ButtonState = { color: ButtonColor; active: boolean; text: string };

const saleEnded = computed(() => props.event.saleend && unixTimeToDate(props.event.saleend) < now.value);
const saleNotStarted = computed(() => props.event.salestart && unixTimeToDate(props.event.salestart) > now.value);
const saleEndDate = computed(() => formatDateWithOffset(
  props.event.saleend,
  props.event.saleend_gmt_offset,
  { locale: locale.value, showTime: true, numeric: true },
));
const doorsaleButton = computed(() => props.event.doorsale_button);

const baseButtonState = computed<ButtonState>(() => {  
  if (isExternalTicketsale.value) {
    if (props.event.external_ticketsale_soldout) {
      return { color: 'muted', active: false, text: $t('event.sold_out') };
    }
    return {
      color: 'primary',
      active: true,
      text: getLocalizedEventValue(props.event, locale.value, 'button_text_buy_tickets') || $t('event.find_tickets'),
    };
  }

  if ((props.event.soldout && !saleEnded.value) && hasWaitlist.value) {
    return { color: 'primary', active: true, text: $t('event.subscribe_to_waitlist') };
  }
  if (props.event.soldout) {
    return { color: 'muted', active: false, text: $t('event.sold_out') };
  }
  if (saleNotStarted.value) {
    const formated_date = formatDate(getSalestart(props.event), { locale: locale.value, showTime: true });
    if (formated_date && formated_date !== 'Invalid date') {
      return { color: 'muted', active: false, text: $t('event.sale_not_started_with_date', { date: formated_date }) };
    }else {
      return { color: 'muted', active: false, text: $t('event.sale_not_started') };
    }
  }
  if (saleEnded.value) {
    return { color: 'muted', active: false, text: $t('event.sale_ended') };
  }

  if (doorsaleButton.value) {
    return {
      color: 'muted',
      active: false,
      text: getLocalizedEventValue(props.event, locale.value, 'doorsale_button_text') || $t('event.doorsale_button'),
    };
  }
  
  if (props.previewMode) {
    return {
      color: 'muted',
      active: false,
      text: getLocalizedEventValue(props.event, locale.value, 'button_text_find_tickets') || $t('event.find_tickets'),
    };
  }

  return {
    color: 'primary',
    active: true,
    text: getLocalizedEventValue(props.event, locale.value, 'button_text_find_tickets') || $t('event.find_tickets'),
  };
});

// Event level "sso required". As in the old client this comes after every other state and replaces the button text.
const ssoRequired = computed(() => ssoStore.isActive && !!props.event.single_sign_on_required);
const needsSsoLogin = computed(() => ssoRequired.value && !ssoStore.isLoading && !ssoStore.isLoggedIn && ssoStore.isAvailable);

const buttonState = computed<ButtonState>(() => {
  if (ssoRequired.value && ssoStore.isLoading) {
    return { ...baseButtonState.value, active: false };
  }
  if (ssoRequired.value && !ssoStore.canAccessEvent(props.event)) {
    // Logged out: the button starts the login. Logged in without an allowed usertype: nothing the user can do, and the
    // text says what is missing instead of asking for a login the user has already done.
    return {
      color: needsSsoLogin.value ? 'primary' : 'muted',
      active: needsSsoLogin.value,
      text: ssoStore.isLoggedIn ? noAccessButtonText.value : $t('event.login_to_find_tickets'),
    };
  }
  return baseButtonState.value;
});

</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
